import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbAlertTriangle, TbUserCircle } from "react-icons/tb";
import { FaInstagram, FaFacebookF, FaYoutube, FaLinkedinIn } from "react-icons/fa";
import { usePageList } from "../../pageConfig";
import { useAuth } from "../../AuthContext";
import Services from "../../services/Services";
import ConsentLockNotice from "../../components/ConsentLockNotice/ConsentLockNotice";
import { getBillingReadiness } from "../../utils/billingReadiness";
import {
  SECONDARY_CONTACT_TAB,
  SECONDARY_CONTACT_UPDATED_EVENT,
  hasAcceptedStorageOffer,
  isSecondaryContactMissing,
} from "../../utils/secondaryContact";
import "./CustomerHome.css";

import hero1 from "../../assets/musteri-panel-hero-1.webp";
import hero3 from "../../assets/musteri-panel-hero-3.webp";
import hero4 from "../../assets/musteri-panel-hero-4.webp";

const HERO_SLIDES = [hero1, hero3, hero4];
const SLIDE_INTERVAL_MS = 5000;

const normalizeId = (value) =>
  value === null || value === undefined || typeof value === "object" ? "" : String(value).trim();

// ContractsPage.getStatusMeta ile aynı kural seti: müşterinin İMZASI beklenen sözleşmeler.
// (5 = admin onayı bekliyor, imza değil; 1/aktif = tamamlanmış; red/iptal sayılmaz;
// bilinmeyen statü sayfada da "İmza Bekliyor" kabul edilir.)
function isContractAwaitingSignature(row) {
  // Depo girişi bekleyen sözleşme henüz imzalanamaz — rozet saymaz.
  if (String(row?.awaitingEntry ?? "") === "1") return false;
  // Alan adları uçtan farklı biçimlerde gelebiliyor (müşteri önekli / snake_case).
  // ContractsPage.normalizeContract ile AYNI aday listesi kullanılmazsa imzalanmış sözleşme
  // "imza bekliyor" sayılıp rozet yanlış çıkıyordu.
  const status = String(
    row?.status ?? row?.contractStatus ?? row?.contract_status ?? row?.signStatus ?? row?.sign_status ?? ""
  ).trim().toLowerCase();
  const hasSignature = Boolean(
    row?.signatureID ||
    row?.signatureId ||
    row?.signature_id ||
    row?.customerSignatureID ||
    row?.customerSignatureId ||
    row?.customer_signature_id ||
    row?.signedDateTime ||
    row?.signedAt ||
    row?.customerSignedDateTime ||
    row?.customer_signed_date_time ||
    row?.signatureURL ||
    row?.signatureUrl ||
    row?.signature_url
  );
  if (status === "5") return false;
  if (["3", "2", "pending", "waiting"].includes(status)) return true;
  if (hasSignature || status === "signed") return false;
  if (["rejected", "reddedildi", "cancelled", "canceled"].includes(status)) return false;
  if (["1", "active", "aktif"].includes(status)) return false;
  return true;
}

// Teklif sahibi (AdminOffersPage.resolveOfferOwnerId ile aynı aday sırası).
function resolveOfferOwnerId(offer) {
  const candidates = [
    offer?.userID, offer?.userId, offer?.customerID, offer?.customerId,
    offer?.request?.userID, offer?.request?.userId,
    offer?.request?.customerID, offer?.request?.customerId,
  ];
  for (const candidate of candidates) {
    const id = normalizeId(candidate);
    if (id) return id;
  }
  return "";
}

// Müşteri anasayfası: hero carousel + menü kısayolları. Kısayollar sidebar
// konfigürasyonundan (usePageList) türetilir; menü değişince burası da değişir.
export default function CustomerHome() {
  const navigate = useNavigate();
  const pageList = usePageList();
  // Zorunlu onay kilidi: KVKK / Kullanıcı Sözleşmesi (güncel sürüm) onaylanmadan panel
  // kilitlenir — uyarı burada gösterilir, onaylar buradaki modaldan verilir.
  const { customerMissingConsents = [], isCustomerConsentLocked } = useAuth() || {};

  const [activeSlide, setActiveSlide] = useState(0);
  // Fatura bilgisi / ödeme yöntemi eksikse uyarı bandı gösterilir (teklif kabulünde de modal çıkar).
  const [billingReadiness, setBillingReadiness] = useState({ known: false, ready: true });
  // İkincil irtibat kişileri (admin'in eklediği + müşterinin eklediği AYNI liste; kullanıcı
  // bilgisinden okunur). undefined = henüz bilinmiyor → bant çıkmaz.
  const [secondaryContact, setSecondaryContact] = useState(undefined);
  // Zorunluluk koşulu: kabul edilmiş DEPOLAMA teklifi (undefined = henüz bilinmiyor → bant yok).
  const [acceptedStorageOffer, setAcceptedStorageOffer] = useState(undefined);
  const loadSecondaryContact = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    Services.GetUserInfos(token)
      .then((res) => {
        if (!res?.data?.status) return;
        const list = res.data.result?.secondaryContacts;
        setSecondaryContact(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadSecondaryContact();
    window.addEventListener(SECONDARY_CONTACT_UPDATED_EVENT, loadSecondaryContact);
    return () => window.removeEventListener(SECONDARY_CONTACT_UPDATED_EVENT, loadSecondaryContact);
  }, [loadSecondaryContact]);
  // path → müşteriden aksiyon bekleyen kayıt sayısı (kartlarda kırmızı rozet)
  const [badges, setBadges] = useState({});
  const [paused, setPaused] = useState(false);
  const touchStartXRef = useRef(null);

  const goToSlide = useCallback((index) => {
    setActiveSlide((index + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);

  // Otomatik ilerleme — yalnız aktif dokunuş sırasında durur. Hover'da DURMAZ:
  // imleç hero üzerinde unutulunca (ya da mobil tarayıcının tap'te ürettiği sentetik
  // mouseenter'la) carousel'in sonsuza dek takılı kalmasına yol açıyordu.
  // activeSlide bağımlılığı, elle geçişte 5sn'lik sayacı sıfırlar.
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [paused, activeSlide]);

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
    setPaused(true);
  };

  // touchcancel'da da çağrılır — kaydırma sistem hareketiyle kesilirse paused takılı kalmasın.
  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    setPaused(false);
    if (startX === null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? startX;
    const delta = endX - startX;
    if (Math.abs(delta) < 40) return;
    goToSlide(activeSlide + (delta < 0 ? 1 : -1));
  };

  // Rozetler: imza bekleyen sözleşme, onay bekleyen teklif, ödenmemiş hizmet bedeli.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    let cancelled = false;
    const currentUserID = normalizeId(localStorage.getItem("userID"));

    Promise.allSettled([
      Services.GetContracts(token, { light: 1 }),
      Services.GetAllOffers(token),
      Services.GetPaymentLedger(token),
      Services.GetUserInfos(token),
    ]).then(([contractsRes, offersRes, ledgerRes, infoRes]) => {
      if (cancelled) return;
      const next = {};

      // Fatura + ödeme yöntemi hazırlığı (kayıtlı kartlar ledger yanıtından gelir).
      const userInfo = infoRes.status === "fulfilled" && infoRes.value?.data?.status
        ? infoRes.value.data.result
        : null;
      const cards = ledgerRes.status === "fulfilled" && ledgerRes.value?.data?.status
        && Array.isArray(ledgerRes.value.data.result?.cards)
        ? ledgerRes.value.data.result.cards
        : null;
      setBillingReadiness(getBillingReadiness({ userInfo, cards }));

      if (contractsRes.status === "fulfilled" && contractsRes.value?.data?.status) {
        const rows = Array.isArray(contractsRes.value.data.result) ? contractsRes.value.data.result : [];
        const count = rows.filter(isContractAwaitingSignature).length;
        if (count > 0) next["/my-contracts"] = count;
      }

      if (offersRes.status === "fulfilled" && offersRes.value?.data?.status) {
        const rows = Array.isArray(offersRes.value.data.result) ? offersRes.value.data.result : [];
        setAcceptedStorageOffer(hasAcceptedStorageOffer(rows, currentUserID));
        // 4 = Onay Bekleniyor: teklif gönderildi, müşterinin yanıtı bekleniyor.
        const count = rows.filter(
          (offer) =>
            String(offer?.status ?? "") === "4" &&
            (!currentUserID || resolveOfferOwnerId(offer) === currentUserID)
        ).length;
        if (count > 0) next["/my-offers"] = count;
      }

      if (ledgerRes.status === "fulfilled" && ledgerRes.value?.data?.status) {
        const rows = Array.isArray(ledgerRes.value.data.result?.rows) ? ledgerRes.value.data.result.rows : [];
        const count = rows.filter(
          (row) => row?.entryType === "debt" && Number(row?.paid) !== 1
        ).length;
        if (count > 0) next["/my-payments"] = count;
      }

      setBadges(next);
    });

    return () => { cancelled = true; };
  }, []);

  const greetingName = useMemo(() => {
    const name = String(localStorage.getItem("name") || "").trim();
    return name ? name.split(" ")[0] : "";
  }, []);

  // Sidebar'daki müşteri sayfaları (Anasayfa hariç) + Profil kartı.
  const menuItems = useMemo(() => {
    const items = [];
    pageList.forEach((section) => {
      (section.items || []).forEach((item) => {
        if (!item?.path || item.path === "/homepage") return;
        items.push({ text: item.text, icon: item.icon, path: item.path });
      });
    });
    items.push({ text: "Profil", icon: <TbUserCircle strokeWidth={1} />, path: "/profile" });
    return items;
  }, [pageList]);

  return (
    <div className="customer-home">
      <ConsentLockNotice consents={customerMissingConsents} />

      {billingReadiness.known && !billingReadiness.ready && !isCustomerConsentLocked && (
        <section className="consent-lock" aria-live="polite">
          <div className="consent-lock-head">
            <TbAlertTriangle size={22} />
            <div>
              <strong>Fatura ve ödeme bilgilerinizi tamamlayın</strong>
              <p>
                Teklifiniz kabul edildi. Faturalandırma ve tahsilat işlemlerinin sorunsuz
                ilerlemesi için aşağıdaki bilgileri tanımlamanız gerekiyor.
              </p>
            </div>
          </div>
          <div className="consent-lock-items">
            {billingReadiness.billingMissing && (
              <div className="consent-lock-item">
                <span className="consent-lock-title">Fatura Bilgileri</span>
                <button type="button" onClick={() => navigate("/profile", { state: { profileTab: "billing" } })}>
                  Bilgileri Gir
                </button>
              </div>
            )}
            {billingReadiness.paymentMissing && (
              <div className="consent-lock-item">
                <span className="consent-lock-title">Ödeme Yöntemi</span>
                <button type="button" onClick={() => navigate("/my-payments", { state: { openCards: true } })}>
                  Kart Ekle
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* İkincil irtibat kişisi eklenmemiş — YALNIZ depolama teklifini kabul etmiş müşteride
          (Sidebar'daki "Anasayfa" rozetiyle aynı koşul). */}
      {acceptedStorageOffer === true && isSecondaryContactMissing(secondaryContact) && !isCustomerConsentLocked && (
        <section className="consent-lock" aria-live="polite">
          <div className="consent-lock-head">
            <TbAlertTriangle size={22} />
            <div>
              <strong>İkincil irtibat kişinizi ekleyin</strong>
              <p>
                Size ulaşamadığımız durumlarda ulaşabileceğimiz ikinci bir irtibat kişisi eklemeniz gerekiyor.
              </p>
            </div>
          </div>
          <div className="consent-lock-items">
            <div className="consent-lock-item">
              <span className="consent-lock-title">İkincil İrtibat Kişisi</span>
              <button type="button" onClick={() => navigate("/profile", { state: { profileTab: SECONDARY_CONTACT_TAB } })}>
                Kişi Ekle
              </button>
            </div>
          </div>
        </section>
      )}

      {/* İmzası beklenen sözleşme uyarısı — rozetle aynı sayaçtan beslenir
          (isContractAwaitingSignature; depo girişi bekleyenler sayılmaz). */}
      {badges["/my-contracts"] > 0 && !isCustomerConsentLocked && (
        <section className="consent-lock" aria-live="polite">
          <div className="consent-lock-head">
            <TbAlertTriangle size={22} />
            <div>
              <strong>İmza bekleyen sözleşmeleriniz var</strong>
              <p>
                {badges["/my-contracts"]} sözleşme imzanızı bekliyor. Süreçlerinizin
                ilerleyebilmesi için lütfen sözleşmelerinizi imzalayın.
              </p>
            </div>
          </div>
          <div className="consent-lock-items">
            <div className="consent-lock-item">
              <span className="consent-lock-title">Sözleşmelerim</span>
              <button type="button" onClick={() => navigate("/my-contracts")}>
                Sözleşmeleri İmzala
              </button>
            </div>
          </div>
        </section>
      )}

      <section
        className="customer-home-hero"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        aria-label="Tanıtım görselleri"
      >
        {HERO_SLIDES.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`customer-home-hero-slide ${index === activeSlide ? "active" : ""}`}
            loading={index === 0 ? "eager" : "lazy"}
            draggable={false}
          />
        ))}
        <div className="customer-home-hero-overlay" />

        <div className="customer-home-hero-content">
          <span className="customer-home-hero-eyebrow">TROY DEPO</span>
          <h1 className="customer-home-hero-title">
            {greetingName ? `Hoş geldin, ${greetingName}` : "Hoş geldin"}
          </h1>
          <p className="customer-home-hero-subtitle">
            Depolama ve nakliye işlemlerini tek yerden yönet.
          </p>
          <button
            type="button"
            className="customer-home-hero-cta"
            disabled={isCustomerConsentLocked}
            title={isCustomerConsentLocked ? "Önce güncel metinleri onaylamanız gerekiyor" : undefined}
            onClick={() => !isCustomerConsentLocked && navigate("/create-request")}
          >
            Talep Oluştur
          </button>
        </div>

        <div className="customer-home-hero-bars" role="tablist" aria-label="Görsel seçimi">
          {HERO_SLIDES.map((src, index) => (
            <button
              key={src}
              type="button"
              role="tab"
              aria-selected={index === activeSlide}
              aria-label={`Görsel ${index + 1}`}
              className={`customer-home-hero-bar ${index === activeSlide ? "active" : ""}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </section>

      <section className="customer-home-menu" aria-label="Hızlı erişim">
        <span className="customer-home-menu-title">Hızlı Erişim</span>
        <div className="customer-home-menu-grid">
          {menuItems.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`customer-home-menu-card${isCustomerConsentLocked ? " customer-home-menu-card--locked" : ""}`}
              disabled={isCustomerConsentLocked}
              title={isCustomerConsentLocked ? "Önce güncel metinleri onaylamanız gerekiyor" : undefined}
              onClick={() => !isCustomerConsentLocked && navigate(item.path)}
            >
              <span className="customer-home-menu-icon">{item.icon}</span>
              <span className="customer-home-menu-label">{item.text}</span>
              {badges[item.path] > 0 && (
                <span className="customer-home-menu-badge" title="Sizden aksiyon bekleyen kayıt">
                  {badges[item.path] > 99 ? "99+" : badges[item.path]}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      <section className="customer-home-social" aria-label="Bizi takip edin">
        <span className="customer-home-social-title">Bizi Takip Edin</span>
        <div className="customer-home-social-row">
          {[
            { key: "instagram", label: "Instagram", href: "https://www.instagram.com/troydepo/", icon: <FaInstagram /> },
            { key: "facebook", label: "Facebook", href: "https://www.facebook.com/troydepotr/", icon: <FaFacebookF /> },
            { key: "youtube", label: "YouTube", href: "https://www.youtube.com/@troydepo", icon: <FaYoutube /> },
            { key: "linkedin", label: "LinkedIn", href: "https://www.linkedin.com/company/troydepo/about/", icon: <FaLinkedinIn /> },
          ].map((social) => (
            <a
              key={social.key}
              className={`customer-home-social-logo customer-home-social-logo--${social.key}`}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.label}
              title={social.label}
            >
              <span className="customer-home-social-mark">{social.icon}</span>
              <span className="customer-home-social-word">{social.label}</span>
            </a>
          ))}
        </div>
      </section>

    </div>
  );
}
